import React, {useEffect, useMemo, useRef, useState} from 'react';
import {ScrollView, TouchableOpacity, View, StyleSheet} from 'react-native';
import CCText from '../CCText';
import ContextMenu from '@/Components/ContextMenu';
import {ChessConfirm} from '@/Components/ChessModal';
import {truncateMovesForReveal} from './truncateMovesForReveal';
import {
  BranchMove,
  flattenMoveTree,
  getDeleteImpactCount,
} from '@/Screens/Study/utils/moveTreeBranchOps';
import {deleteMoveLocally} from '@/Screens/Study/utils/moveTreeOps';
import {
  buildNavIndex,
  getFirstVariation,
  getLineEnd,
  getMainlineForkAncestor,
  getNextMove,
  getPrevMove,
} from '@/Screens/Study/utils/moveNavigation';
import {isBadMove} from '@/Screens/Study/utils/lessonMoves';
import type {default as useMoveTreeUndoHook} from '@/Hooks/ManageMoves/useMoveTreeUndo';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';

/**
 * Move tree laid out the way lichess's study/analysis move list is
 * (re-implemented from scratch, themed to our palette):
 *
 * - The mainline is a 3-column score sheet: index | white | black.
 * - A move that carries a comment or variations "interrupts" the sheet:
 *   its row ends with a `…` placeholder in the partner cell, a full-width
 *   interrupt block renders underneath (comment first, then every
 *   variation), and — when white was the interrupted side — the black
 *   reply resumes on a fresh row as `N… <move>` with a `…` white cell.
 * - Variations are ALWAYS visible (no fork toggles): each top-level line
 *   gets its own row in the interrupt with a colored vertical guide rail,
 *   and a sub-variation is spliced inline, in parentheses, at the exact
 *   move it forks from — nested parens nest further, inheriting the
 *   parent line's color family so a deep tree still reads as one lineage.
 * - The active move is a solid filled highlight box (our gold, where
 *   lichess uses blue), comments render italic inline, and NAG glyphs
 *   (!, ??, ⩲ …) render as colored suffixes on their move.
 *
 * Keyboard navigation is path-aware (utils/moveNavigation.ts, shared with
 * the study screen's nav buttons and autoplay): ←/→ walk the line you are
 * on, ↑/↓ jump to the start/end of it, Shift+→ enters the first variation
 * at the active move and Shift+← climbs back out to the mainline — the
 * same bindings lichess's analysis board uses.
 */

/* ─── Design Tokens ───────────────────────────────────────── */
const C = {
  brown: '#4D3F37',
  brownDark: '#1E1916',
  brownMedium: '#6b584d',
  grey: '#A8A7A6',
  greyDark: '#7D7D7D',
  greyLight: '#C0BCBA',
};

const SURFACE = CCColors.Primary.Light;
// Was 0.065 and drawn around every cell (index/white/black all boxed) - a
// full spreadsheet grid, at odds with the borderless/flat-row language the
// rest of the redesign settled on (Participants' rows). Lighter now, and
// only used as a soft divider between pairs rather than a box around every
// cell - see pairRow/indexCell/moveCell below.
const BORDER = updateColorWithOpacity(CCColors.TextColor.Dark, 0.05);
const INTERRUPT_BG = updateColorWithOpacity(CCColors.TextColor.Dark, 0.024);
const INDEX_WIDTH = 26;

// Was a bespoke '#FFE082'/'#BF9000' pair - a different gold than the one
// every other "active/selected" state in the redesign uses (Participants'
// selected row, the workspace tab pills), so the current move stood out as
// visually off-brand. Now derived from the same CCColors.Primary.Default
// the rest of the page uses.
const ACTIVE_BG = CCColors.Primary.Default;
const ACTIVE_ACCENT = CCColors.Workbook.GoldDark;
const HOVER_BG = updateColorWithOpacity(CCColors.Primary.Default, 0.12);

const FONT_SIZE_BY_DEPTH = [12.5, 11, 10.5, 10, 9.5];
const COLOR_BY_DEPTH = [
  C.brownDark,
  C.brown,
  C.brownMedium,
  C.greyDark,
  C.grey,
];

/** Distinct hue per top-level variation; nested sub-variations reuse the
 * parent's color family so lineage stays legible. */
const BRANCH_COLORS = [
  {
    rail: CCColors.Green,
    deep: CCColors.Workbook.TealDeep,
    wash: updateColorWithOpacity(CCColors.Green, 0.13),
  },
  {
    rail: CCColors.Workbook.OrangeAnnotation,
    deep: CCColors.Workbook.OrangeDeep,
    wash: updateColorWithOpacity(CCColors.Workbook.OrangeAnnotation, 0.13),
  },
  {
    rail: CCColors.Workbook.PurpleAnnotation,
    deep: CCColors.Workbook.PurpleDeep,
    wash: updateColorWithOpacity(CCColors.Workbook.PurpleAnnotation, 0.13),
  },
  {
    rail: CCColors.Workbook.Pink,
    deep: CCColors.Workbook.PinkDeep,
    wash: updateColorWithOpacity(CCColors.Workbook.Pink, 0.13),
  },
  {
    rail: CCColors.Workbook.BrownGold,
    deep: CCColors.Workbook.BrownGoldDeep,
    wash: updateColorWithOpacity(CCColors.Workbook.BrownGold, 0.13),
  },
];

const LESSON_BRANCH_COLORS = {
  bad: {
    rail: CCColors.Error,
    deep: CCColors.Workbook.CrimsonDeep,
    wash: updateColorWithOpacity(CCColors.Error, 0.14),
  },
  alternate: {
    rail: CCColors.Progress.Bar,
    deep: CCColors.Workbook.OliveDeep,
    wash: updateColorWithOpacity(CCColors.Progress.Bar, 0.15),
  },
};

function branchColorFor(index: number) {
  return BRANCH_COLORS[index % BRANCH_COLORS.length];
}
function fontSizeForDepth(depth: number) {
  return FONT_SIZE_BY_DEPTH[Math.min(depth, FONT_SIZE_BY_DEPTH.length - 1)];
}
function colorForDepth(depth: number) {
  return COLOR_BY_DEPTH[Math.min(depth, COLOR_BY_DEPTH.length - 1)];
}

/** Maps annotation icon names to lichess glyph symbols. */
const GLYPH_SYMBOL_BY_ICON_NAME: Record<string, string> = {
  'Good move': '!',
  Mistake: '?',
  'Brilliant move': '!!',
  Blunder: '??',
  'Interesting move': '!?',
  'Dubious move': '?!',
  'Only move': '□',
  Zugzwang: '⨀',
  'Equal position': '=',
  'Unclear position': '∞',
  'White is slightly better': '⩲',
  'Black is slightly better': '⩱',
  'White is better': '±',
  'Black is better': '∓',
  'White is winning': '+−',
  'Black is winning': '−+',
  Development: '↑↑',
  Initiative: '↑',
  Attack: '→',
  'Counter-play': '⇆',
  'Time trouble': '⊕',
  'With compensation': '=∞',
  'With the idea': 'Δ',
};

/** Quality glyphs get semantic colors; positional glyphs stay neutral. */
const MOVE_QUALITY_GLYPH_COLOR: Record<string, string> = {
  '??': CCColors.Red,
  '?': CCColors.Progress.Bar,
  '?!': CCColors.Blue,
  '!?': CCColors.Blue,
  '!': CCColors.Green,
  '!!': CCColors.Green,
};

function glyphSymbolForIcon(annotationIcon?: string | null): string | null {
  if (!annotationIcon) {
    return null;
  }
  return GLYPH_SYMBOL_BY_ICON_NAME[annotationIcon] || null;
}

function glyphColor(symbol: string, depth: number): string {
  return MOVE_QUALITY_GLYPH_COLOR[symbol] || colorForDepth(depth);
}

function commentAccentColor(text: string): string {
  if (text.startsWith('Blunder.')) {
    return CCColors.Red;
  }
  if (text.startsWith('Mistake.')) {
    return CCColors.Progress.Bar;
  }
  if (text.startsWith('Inaccuracy.')) {
    return CCColors.Blue;
  }
  return C.brownMedium;
}

/* ─── Helpers ─────────────────────────────────────────────── */

function movePrefix(fen: string | undefined, isFirstInLine: boolean): string {
  if (!fen) {
    return '';
  }
  const parts = fen.split(' ');
  const activeColor = parts[1];
  const fullmove = parseInt(parts[5], 10) || 1;
  const whiteJustMoved = activeColor === 'b';
  const moveNum = whiteJustMoved ? fullmove : fullmove - 1;
  if (whiteJustMoved) {
    return `${moveNum}.`;
  }
  return isFirstInLine ? `${moveNum}…` : '';
}

function isWhitesTurn(fen?: string) {
  return fen?.split(' ')?.[1] === 'w';
}

function hasInterrupt(move: any): boolean {
  return (
    !!(move?.san || move?.move) &&
    (!!move?.annotation || (move?.child_moves?.length ?? 0) > 0)
  );
}

/* ─── Types ───────────────────────────────────────────────── */

interface VariationNode {
  id: string | number;
  move: any;
  prevMove: any;
  branches: VariationBranch[];
}
interface VariationBranch {
  id: string;
  nodes: VariationNode[];
}

type MoveHandlers = {
  activeMove: any;
  onMoveClick: (move: any, prevMove: any) => void;
  onPromote: (node: VariationNode) => void;
  onDelete: (node: VariationNode) => void;
  isOwner?: boolean;
  highlightedMoveIds?: Set<string | number>;
  scrollToMoveId?: string | number;
  lessonMode?: boolean;
  onSetMoveRole?: (move: any, role: 'alternate' | 'bad') => void;
};

/** Every entry in `children` is a variation root: follow child_moves[0] as
 * that line's continuation, anything past slot 0 forks again beneath it. */
function buildBranches(
  children: any[] | undefined,
  prevMove: any,
): VariationBranch[] {
  const buildBranchFromRoot = (rootMove: any, rootPrevMove: any) => {
    const nodes: VariationNode[] = [];
    let cursor = rootMove;
    let cursorPrev = rootPrevMove;

    while (cursor) {
      const childMoves = cursor?.child_moves || [];
      const continuation = childMoves[0];
      const splitBranches = childMoves.slice(1);

      nodes.push({
        id: cursor?.id ?? `${cursor?.fen || cursor?.san}`,
        move: cursor,
        prevMove: cursorPrev,
        branches: buildBranches(splitBranches, cursor),
      });

      if (!continuation) {
        break;
      }
      cursorPrev = cursor;
      cursor = continuation;
    }

    return {id: `br-${nodes[0]?.id}`, nodes};
  };

  return (children || []).map(child => buildBranchFromRoot(child, prevMove));
}

/* ─── MoveToken ──────────────────────────────────────────── */
function MoveToken({
  prefix,
  san,
  isEmpty,
  isActive,
  depth,
  annotationIcon,
  onPress,
  isHighlighted,
  shouldAutoScroll,
  menuOptions,
}: {
  prefix?: string;
  san?: string;
  isEmpty?: boolean;
  isActive: boolean;
  depth: number;
  annotationIcon?: string;
  onPress?: () => void;
  isHighlighted?: boolean;
  shouldAutoScroll?: boolean;
  menuOptions: {label: string; onClick: () => void}[];
}) {
  const chipRef = useRef<any>(null);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if ((isActive || shouldAutoScroll) && chipRef.current) {
      setTimeout(() => {
        chipRef.current?.scrollIntoView?.({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }, 50);
    }
  }, [isActive, shouldAutoScroll]);

  if (isEmpty || !san) {
    return (
      <View style={styles.emptyToken}>
        <CCText style={styles.emptyTokenText}>{'…'}</CCText>
      </View>
    );
  }

  const fontSize = fontSizeForDepth(depth);
  const glyphSymbol = glyphSymbolForIcon(annotationIcon);

  const body = (
    <TouchableOpacity
      ref={chipRef}
      onPress={onPress}
      activeOpacity={0.6}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.token,
        isHighlighted && !isActive && styles.tokenHighlighted,
        hovered && !isActive && {backgroundColor: HOVER_BG},
        isActive && styles.tokenActive,
      ]}>
      {!!prefix && (
        <CCText
          style={[
            styles.tokenPrefix,
            {fontSize: fontSize * 0.82},
            isActive && styles.tokenPrefixActive,
          ]}>
          {prefix}
        </CCText>
      )}
      <CCText
        style={[
          styles.tokenText,
          {fontSize, color: isActive ? C.brownDark : colorForDepth(depth)},
          isActive && styles.tokenTextActive,
        ]}>
        {san}
        {glyphSymbol && (
          <CCText
            style={[
              styles.glyphSuffix,
              {
                fontSize: fontSize * 0.82,
                color: isActive
                  ? ACTIVE_ACCENT
                  : glyphColor(glyphSymbol, depth),
              },
            ]}>
            {glyphSymbol}
          </CCText>
        )}
      </CCText>
    </TouchableOpacity>
  );

  if (!menuOptions.length) {
    return body;
  }
  return (
    <ContextMenu menuOptions={menuOptions} style={styles.contextMenuWrapper}>
      {body}
    </ContextMenu>
  );
}

/* ─── CommentBlock (mainline interrupt comment) ───
 * Was a bare left-border stripe with no fill - now a tinted note card (the
 * accent color as a soft wash + a slightly bolder left rail), matching the
 * tinted-card language the rest of the redesign uses for callouts (the
 * flyouts' count badges) instead of a plain ruled line. */
function CommentBlock({text}: {text: string}) {
  const accent = commentAccentColor(text);
  return (
    <View
      style={[
        styles.commentBlock,
        {
          borderLeftColor: accent,
          backgroundColor: updateColorWithOpacity(accent, 0.08),
        },
      ]}>
      <CCText style={[styles.commentBlockText, {color: accent}]}>{text}</CCText>
    </View>
  );
}

/* ─── Paren ───────────────────────────────────────────────── */
function Paren({
  char,
  depth,
  color,
  testID,
}: {
  char: '(' | ')';
  depth: number;
  color: string;
  testID?: string;
}) {
  return (
    <CCText
      testID={testID}
      style={[styles.paren, {fontSize: fontSizeForDepth(depth) * 1.05, color}]}>
      {char}
    </CCText>
  );
}

/* ─── LineFlow ────────────────────────────────────────────────
 * The flowing body of one variation line: its moves in order, each
 * followed by its inline comment, and — exactly at any move that owns
 * further alternatives — those sub-variations spliced in as parenthesized
 * inline segments (recursing deeper), the way lichess interleaves nested
 * lines at their fork point instead of appending them after the chain. */
function LineFlow({
  nodes,
  depth,
  handlers,
  colorFamily,
  lessonColor,
}: {
  nodes: VariationNode[];
  depth: number;
  handlers: MoveHandlers;
  colorFamily: number;
  lessonColor?: {rail: string; deep: string; wash: string} | null;
}) {
  const {
    activeMove,
    onMoveClick,
    onPromote,
    onDelete,
    isOwner,
    highlightedMoveIds,
    scrollToMoveId,
  } = handlers;

  return (
    <>
      {nodes.map((node, idx) => {
        const move = node.move;
        const isFirstInLine = idx === 0;
        const prefix = movePrefix(move?.fen, isFirstInLine);
        const isActive = activeMove?.id
          ? activeMove.id === move?.id
          : activeMove?.fen === move?.fen;
        const menuOptions = isOwner
          ? [
              {
                label: handlers.lessonMode
                  ? 'Mark perfect (promote to mainline)'
                  : 'Promote to mainline',
                onClick: () => onPromote(node),
              },
              ...(handlers.lessonMode && handlers.onSetMoveRole
                ? isBadMove(move)
                  ? [
                      {
                        label: 'Mark as acceptable alternate',
                        onClick: () =>
                          handlers.onSetMoveRole!(move, 'alternate'),
                      },
                    ]
                  : [
                      {
                        label: 'Mark as mistake',
                        onClick: () => handlers.onSetMoveRole!(move, 'bad'),
                      },
                    ]
                : []),
              {label: 'Delete', onClick: () => onDelete(node)},
            ]
          : [];

        return (
          <React.Fragment key={String(node.id)}>
            <MoveToken
              prefix={prefix}
              san={move?.san || move?.move}
              isActive={isActive}
              depth={depth}
              annotationIcon={move?.annotation_icon}
              onPress={() => onMoveClick(move, node.prevMove)}
              isHighlighted={!!move?.id && highlightedMoveIds?.has(move.id)}
              shouldAutoScroll={!!move?.id && move.id === scrollToMoveId}
              menuOptions={menuOptions}
            />
            {!!move?.annotation && (
              <CCText
                style={[
                  styles.inlineComment,
                  {
                    fontSize: fontSizeForDepth(depth) * 0.85,
                    color: commentAccentColor(move.annotation),
                  },
                ]}>
                {move.annotation}
              </CCText>
            )}
            {node.branches.map(branch => (
              <InlineLine
                key={branch.id}
                branch={branch}
                depth={depth + 1}
                handlers={handlers}
                colorFamily={colorFamily}
                lessonColor={lessonColor}
              />
            ))}
          </React.Fragment>
        );
      })}
    </>
  );
}

/* ─── InlineLine ──────────────────────────────────────────────
 * A nested sub-variation, rendered inline inside its parent's flow as
 * `( … )`. Parens carry the parent lineage's color so "this aside belongs
 * to the teal line" reads at a glance even three levels deep. */
function InlineLine({
  branch,
  depth,
  handlers,
  colorFamily,
  lessonColor,
}: {
  branch: VariationBranch;
  depth: number;
  handlers: MoveHandlers;
  colorFamily: number;
  lessonColor?: {rail: string; deep: string; wash: string} | null;
}) {
  const color = lessonColor || branchColorFor(colorFamily);
  return (
    <>
      <Paren
        char="("
        depth={depth}
        color={color.rail}
        testID={`paren-open-${branch.id}`}
      />
      <LineFlow
        nodes={branch.nodes}
        depth={depth}
        handlers={handlers}
        colorFamily={colorFamily}
        lessonColor={lessonColor}
      />
      <Paren char=")" depth={depth} color={color.rail} />
    </>
  );
}

/* ─── Lines / VariationLine ─────────────────────────────────────
 * Every top-level variation off a mainline move renders immediately (no
 * toggles — matching lichess, where each line off the mainline is always
 * visible) as its own row in the interrupt block: a colored vertical
 * guide rail on the left, then the line's moves flowing and wrapping.
 * Sibling lines cycle through our workbook hues so "which line is this"
 * reads at a glance; in a lesson chapter the branch's *role* decides the
 * dressing instead (wrong answer red / acceptable alternate amber) plus a
 * small role tag, so authoring stays legible. */
function Lines({
  branches,
  depth,
  handlers,
}: {
  branches: VariationBranch[];
  depth: number;
  handlers: MoveHandlers;
}) {
  if (!branches.length) {
    return null;
  }
  return (
    <View style={styles.lines}>
      {branches.map((branch, idx) => (
        <VariationLine
          key={branch.id}
          branch={branch}
          depth={depth}
          handlers={handlers}
          colorFamily={idx}
        />
      ))}
    </View>
  );
}

function VariationLine({
  branch,
  depth,
  handlers,
  colorFamily,
}: {
  branch: VariationBranch;
  depth: number;
  handlers: MoveHandlers;
  colorFamily: number;
}) {
  const branchRootMove = branch.nodes[0]?.move;
  const lessonRole = handlers.lessonMode
    ? isBadMove(branchRootMove)
      ? ('bad' as const)
      : ('alternate' as const)
    : null;
  const lessonColor = lessonRole ? LESSON_BRANCH_COLORS[lessonRole] : null;
  const color = lessonColor || branchColorFor(colorFamily);

  return (
    <View style={styles.line} testID={`variation-line-${branch.id}`}>
      <View
        testID={`line-rail-${branch.id}`}
        style={[styles.lineRail, {backgroundColor: color.rail}]}
      />
      <View style={styles.lineRow}>
        {lessonRole && (
          <View style={[styles.roleTag, {backgroundColor: color.wash}]}>
            <CCText style={[styles.roleTagText, {color: color.deep}]}>
              {lessonRole === 'bad' ? 'Wrong answer' : 'Alternate'}
            </CCText>
          </View>
        )}
        <LineFlow
          nodes={branch.nodes}
          depth={depth}
          handlers={handlers}
          colorFamily={colorFamily}
          lessonColor={lessonColor}
        />
      </View>
    </View>
  );
}

/* ─── InterruptSection (one move's comment + variations, full width) ───
 * A mainline node's child_moves are ALL alternatives to its successor (the
 * mainline continuation is the next top-level entry — see
 * utils/moveTreeOps.ts). Comment first, then every variation line —
 * always visible, like lichess. */
function InterruptSection({
  move,
  handlers,
}: {
  move: any;
  handlers: MoveHandlers;
}) {
  const hasComment = !!move?.annotation;
  const branches = useMemo(
    () => buildBranches(move?.child_moves ?? [], move),
    [move],
  );
  if (!hasComment && !branches.length) {
    return null;
  }
  return (
    <View style={styles.interruptSection}>
      {hasComment && <CommentBlock text={move.annotation} />}
      <Lines branches={branches} depth={1} handlers={handlers} />
    </View>
  );
}

/* ─── PairRow ───────────────────────────────────────────────
 * [move-number] [white] [black] — either side may be a `…` placeholder
 * when the score sheet is interrupted (lichess's `5. Nf3 …` /
 * `5… b5` continuation rows). */
function PairRow({
  number,
  white,
  black,
  whitePlaceholder,
  blackPlaceholder,
  activeMove,
  onWhitePress,
  onBlackPress,
  isOwner,
  onDelete,
  highlightedMoveIds,
  scrollToMoveId,
}: {
  number: number;
  white?: any;
  black?: any;
  whitePlaceholder?: boolean;
  blackPlaceholder?: boolean;
  activeMove: any;
  onWhitePress: () => void;
  onBlackPress: () => void;
  isOwner?: boolean;
  onDelete: (node: VariationNode) => void;
  highlightedMoveIds?: Set<string | number>;
  scrollToMoveId?: string | number;
}) {
  const isWhiteActive =
    !whitePlaceholder &&
    (activeMove?.id
      ? activeMove.id === white?.id
      : activeMove?.fen === white?.fen);
  const isBlackActive =
    !blackPlaceholder &&
    (activeMove?.id
      ? activeMove.id === black?.id
      : activeMove?.fen === black?.fen);

  const menuFor = (move: any, prevMove: any) =>
    isOwner && move?.id != null
      ? [
          {
            label: 'Delete',
            onClick: () =>
              onDelete({
                id: move.id,
                move,
                prevMove,
                branches: [],
              }),
          },
        ]
      : [];

  return (
    <View style={styles.pairRow}>
      <View style={styles.indexCell}>
        <CCText style={styles.indexText}>{number}</CCText>
      </View>
      <View style={styles.moveCell}>
        <MoveToken
          san={white?.san || white?.move}
          isEmpty={whitePlaceholder || (!white?.san && !white?.move)}
          isActive={isWhiteActive}
          depth={0}
          annotationIcon={white?.annotation_icon}
          isHighlighted={!!white?.id && highlightedMoveIds?.has(white.id)}
          shouldAutoScroll={!!white?.id && white.id === scrollToMoveId}
          menuOptions={whitePlaceholder ? [] : menuFor(white, undefined)}
          onPress={onWhitePress}
        />
      </View>
      <View style={styles.moveCell}>
        <MoveToken
          san={black?.san || black?.move}
          isEmpty={blackPlaceholder || (!black?.san && !black?.move)}
          isActive={isBlackActive}
          depth={0}
          annotationIcon={black?.annotation_icon}
          isHighlighted={!!black?.id && highlightedMoveIds?.has(black.id)}
          shouldAutoScroll={!!black?.id && black.id === scrollToMoveId}
          menuOptions={blackPlaceholder ? [] : menuFor(black, white)}
          onPress={onBlackPress}
        />
      </View>
    </View>
  );
}

/* ─── Main Component ──────────────────────────────────────── */
type MoveTreeUndo = ReturnType<typeof useMoveTreeUndoHook>;

interface WorkbookMovesListProps {
  gameMoves: any[];
  activeMove: any;
  setmoveSelected: (move: any) => void;
  setLoading?: (loading: boolean) => void;
  handleChessMove?: (move: any, arg1: boolean, arg2: string, arg3: any) => void;
  isOwner?: boolean;
  highlightedMoveIds?: Set<string | number>;
  hideMode?: boolean;
  moveUndo: MoveTreeUndo;
  lessonMode?: boolean;
  onSetMoveRole?: (move: any, role: 'alternate' | 'bad') => void;
  /** Called when keyboard nav steps back past move 1 (← / ↑ at the top):
   * the parent owns the chapter's initial position, not this list. */
  onNavigateStart?: () => void;
}

function WorkbookMovesList({
  gameMoves,
  activeMove,
  setmoveSelected,
  setLoading,
  handleChessMove,
  isOwner,
  highlightedMoveIds,
  hideMode,
  moveUndo,
  lessonMode,
  onSetMoveRole,
  onNavigateStart,
}: WorkbookMovesListProps) {
  const scrollRef = useRef<ScrollView>(null);
  const [confirmState, setConfirmState] = useState<{
    visible: boolean;
    move?: any;
    label?: string;
    bodyTxt: string;
  }>({visible: false, bodyTxt: ''});

  useEffect(() => {
    setLoading?.(!!moveUndo?.isLoading);
  }, [moveUndo?.isLoading, setLoading]);

  const prunedGameMoves = useMemo(
    () => deleteMoveLocally(gameMoves, moveUndo?.pendingDeleteMoveId),
    [gameMoves, moveUndo?.pendingDeleteMoveId],
  );

  const effectiveGameMoves = useMemo(
    () =>
      hideMode
        ? truncateMovesForReveal(prunedGameMoves, activeMove?.id)
        : prunedGameMoves,
    [hideMode, prunedGameMoves, activeMove?.id],
  );

  const flatById = useMemo<Map<string, BranchMove>>(
    () => flattenMoveTree(gameMoves),
    [gameMoves],
  );

  // Path-aware navigation index over what is actually *visible* — in
  // conceal mode that's the truncated tree, so the keyboard can never step
  // onto a move the student hasn't revealed.
  const navIndex = useMemo(
    () => buildNavIndex(effectiveGameMoves),
    [effectiveGameMoves],
  );

  const handleSelect = (move: any, prevMove: any) => {
    handleChessMove?.(move, false, '', prevMove);
    setmoveSelected({
      ...move,
      newFen: move?.fen,
      lastFen: prevMove?.fen,
      move: move?.san,
    });
  };

  const requestDelete = (node: VariationNode) => {
    const moveId = node.move?.id;
    if (moveId == null) {
      return;
    }
    const impact = getDeleteImpactCount(flatById, moveId);
    setConfirmState({
      visible: true,
      move: node.move,
      label: node.move?.san ? `${node.move.san} deleted` : 'Move deleted',
      bodyTxt:
        impact > 0
          ? `This will delete this move and the ${impact} move${
              impact > 1 ? 's' : ''
            } that follow it in this line.`
          : 'Delete this move?',
    });
  };

  const confirmDelete = () => {
    if (confirmState.move?.id != null) {
      moveUndo?.queueDelete(confirmState.move.id, confirmState.label);
    }
    setConfirmState({visible: false, bodyTxt: ''});
  };

  const promoteNode = (node: VariationNode) => {
    const moveId = node.move?.id;
    if (moveId == null) {
      return;
    }
    const label = node.move?.san
      ? `${node.move.san} promoted to mainline`
      : 'Promoted to mainline';
    moveUndo?.promoteMove(moveId, flatById, label);
  };

  const displayMoves =
    effectiveGameMoves?.[0]?.fen && isWhitesTurn(effectiveGameMoves[0].fen)
      ? [{} as any, ...effectiveGameMoves]
      : effectiveGameMoves ?? [];

  const scrollToMoveId = useMemo(() => {
    if (!highlightedMoveIds || highlightedMoveIds.size === 0) {
      return undefined;
    }
    for (const entry of navIndex.values()) {
      if (entry.move?.id && highlightedMoveIds.has(entry.move.id)) {
        return entry.move.id;
      }
    }
    return undefined;
  }, [highlightedMoveIds, navIndex]);

  const handlers: MoveHandlers = {
    activeMove,
    onMoveClick: handleSelect,
    onPromote: promoteNode,
    onDelete: requestDelete,
    isOwner,
    highlightedMoveIds,
    scrollToMoveId,
    lessonMode,
    onSetMoveRole,
  };

  /* Keyboard navigation — lichess analysis bindings, path-aware:
   *   ←/→        previous / next move on the current line
   *   ↑/Home     start (chapter position when the parent provides it)
   *   ↓/End      end of the current line
   *   Shift+→    enter the first variation at the active move
   *   Shift+←    climb back out to the mainline fork move            */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const eventTarget = event.target as HTMLElement | null;
      if (
        eventTarget &&
        (['INPUT', 'TEXTAREA', 'SELECT'].includes(eventTarget.tagName) ||
          (eventTarget as any).isContentEditable)
      ) {
        return;
      }

      const isNavKey = [
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ].includes(event.key);
      if (!isNavKey || navIndex.size === 0) {
        return;
      }
      event.preventDefault();

      // Resolve the active node by id, falling back to fen so selections
      // made straight from the board (which may carry only a position)
      // still anchor navigation.
      let activeId: string | number | null =
        activeMove?.id != null && navIndex.has(String(activeMove.id))
          ? activeMove.id
          : null;
      if (activeId == null && activeMove?.fen) {
        for (const entry of navIndex.values()) {
          if (entry.move?.fen === activeMove.fen) {
            activeId = entry.move.id;
            break;
          }
        }
      }

      const selectNode = (node: BranchMove | null | undefined) => {
        if (!node) {
          return;
        }
        const prev = navIndex.get(String(node.id))?.prev ?? null;
        handleSelect(node, prev);
      };

      if (event.shiftKey && event.key === 'ArrowRight') {
        selectNode(getFirstVariation(navIndex, activeId ?? 'start'));
        return;
      }
      if (event.shiftKey && event.key === 'ArrowLeft') {
        selectNode(getMainlineForkAncestor(navIndex, activeId ?? 'start'));
        return;
      }

      switch (event.key) {
        case 'ArrowRight': {
          selectNode(
            getNextMove(effectiveGameMoves, navIndex, activeId ?? 'start'),
          );
          return;
        }
        case 'ArrowLeft': {
          const prev = getPrevMove(navIndex, activeId ?? 'start');
          if (prev === 'start') {
            onNavigateStart?.();
          } else {
            selectNode(prev);
          }
          return;
        }
        case 'ArrowUp':
        case 'Home': {
          if (onNavigateStart) {
            onNavigateStart();
          } else {
            selectNode(effectiveGameMoves?.[0] || null);
          }
          return;
        }
        case 'ArrowDown':
        case 'End': {
          selectNode(
            getLineEnd(effectiveGameMoves, navIndex, activeId ?? 'start'),
          );
          return;
        }
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navIndex, activeMove, effectiveGameMoves, onNavigateStart]);

  const pairs: Array<{white: any; black: any; pairIndex: number}> = [];
  for (let i = 0; i < displayMoves.length; i += 2) {
    pairs.push({
      white: displayMoves[i],
      black: displayMoves[i + 1],
      pairIndex: i / 2,
    });
  }

  if (!gameMoves?.length) {
    return (
      <View style={styles.emptyState}>
        <CCText style={styles.emptyStateText}>No moves yet</CCText>
      </View>
    );
  }

  if (hideMode && !effectiveGameMoves?.length) {
    return (
      <View style={styles.emptyState}>
        <CCText style={styles.emptyStateText}>
          Play a move to reveal the next one
        </CCText>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <ScrollView
        ref={scrollRef}
        style={styles.scroll}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}>
        {pairs.map(({white, black, pairIndex}) => {
          const number = pairIndex + 1;
          const whiteInterrupts = hasInterrupt(white);
          const blackInterrupts = hasInterrupt(black);
          const blackExists = !!(black?.san || black?.move);

          const whitePress = () => {
            if (!white?.san && !white?.move) {
              return;
            }
            const prevIdx = pairIndex * 2 - 1;
            const prev = prevIdx >= 0 ? displayMoves[prevIdx] : undefined;
            handleSelect(white, prev);
          };
          const blackPress = () => {
            if (!blackExists) {
              return;
            }
            handleSelect(black, white);
          };

          // Lichess score-sheet interrupt: when the white move carries a
          // comment or variations, its row closes with a `…` black cell,
          // the interrupt block renders full width, and the black reply
          // resumes on its own `N… <move>` row underneath. A black-side
          // interrupt simply hangs below the completed row.
          if (whiteInterrupts) {
            return (
              <View key={pairIndex}>
                <PairRow
                  number={number}
                  white={white}
                  blackPlaceholder={blackExists}
                  activeMove={activeMove}
                  onWhitePress={whitePress}
                  onBlackPress={() => {}}
                  isOwner={isOwner}
                  onDelete={requestDelete}
                  highlightedMoveIds={highlightedMoveIds}
                  scrollToMoveId={scrollToMoveId}
                />
                <View style={styles.interruptWrap}>
                  <InterruptSection move={white} handlers={handlers} />
                </View>
                {blackExists && (
                  <PairRow
                    number={number}
                    black={black}
                    whitePlaceholder
                    activeMove={activeMove}
                    onWhitePress={() => {}}
                    onBlackPress={blackPress}
                    isOwner={isOwner}
                    onDelete={requestDelete}
                    highlightedMoveIds={highlightedMoveIds}
                    scrollToMoveId={scrollToMoveId}
                  />
                )}
                {blackInterrupts && (
                  <View style={styles.interruptWrap}>
                    <InterruptSection move={black} handlers={handlers} />
                  </View>
                )}
              </View>
            );
          }

          return (
            <View key={pairIndex}>
              <PairRow
                number={number}
                white={white}
                black={black}
                activeMove={activeMove}
                onWhitePress={whitePress}
                onBlackPress={blackPress}
                isOwner={isOwner}
                onDelete={requestDelete}
                highlightedMoveIds={highlightedMoveIds}
                scrollToMoveId={scrollToMoveId}
              />
              {blackInterrupts && (
                <View style={styles.interruptWrap}>
                  <InterruptSection move={black} handlers={handlers} />
                </View>
              )}
            </View>
          );
        })}
      </ScrollView>

      <ChessConfirm
        open={confirmState.visible}
        header="Take back this line?"
        bodyTxt={confirmState.bodyTxt}
        piece="chess-knight"
        pieceRotation={-24}
        accent="red"
        confirmLabel="Yes, Delete"
        confirmIcon="flag-variant"
        handleClose={() => setConfirmState({visible: false, bodyTxt: ''})}
        handleConfirmation={confirmDelete}
      />
    </View>
  );
}

/* ─── Styles ──────────────────────────────────────────────── */
const styles = StyleSheet.create({
  root: {
    flex: 1,
    minHeight: 0,
    backgroundColor: SURFACE,
  },
  scroll: {flex: 1},
  scrollContent: {paddingBottom: 12},

  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    gap: 8,
  },
  emptyStateText: {
    fontSize: 13,
    color: C.greyDark,
    textAlign: 'center',
  },

  /* ── Mainline grid ──
   * Was a full spreadsheet grid (every index/white/black cell individually
   * boxed). Now a plain row with a single soft divider underneath it and
   * more vertical room - the active move's own solid fill (tokenActive)
   * is signal enough without a second, redundant highlight on the cell
   * around it too. */
  pairRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    paddingHorizontal: 4,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: BORDER,
    minHeight: 27,
  },
  indexCell: {
    width: INDEX_WIDTH,
    justifyContent: 'center',
    alignItems: 'center',
  },
  indexText: {
    fontSize: 10,
    color: C.greyDark,
    fontWeight: '500',
  },
  moveCell: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },

  /* ── Move token ── */
  contextMenuWrapper: {flexShrink: 1},
  token: {
    flexDirection: 'row',
    alignItems: 'center',
    // Was paddingVertical 1 / paddingHorizontal 5 / margin 1 / radius 3 (then
    // 5) - a very tight, spreadsheet-dense chip. Roomier now that the grid
    // lines around it are gone (see pairRow/indexCell/moveCell above), so
    // each move reads as its own soft chip rather than text packed into a
    // cell.
    paddingVertical: 3,
    paddingHorizontal: 7,
    borderRadius: 7,
    margin: 2,
    // Web-only: smooth the active/hover highlight sliding between tokens.
    transitionProperty: 'background-color, box-shadow',
    transitionDuration: '0.15s',
  } as any,
  // The active move is a solid filled chip, the way lichess highlights the
  // current move — our gold instead of lichess blue. A soft matching-hue
  // shadow gives it a touch of lift, same "raised" treatment as the app's
  // other active pills (e.g. Study's settingsSubTabBtnActive).
  tokenActive: {
    backgroundColor: ACTIVE_BG,
    shadowColor: ACTIVE_BG,
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.5,
    shadowRadius: 4,
    elevation: 2,
  } as any,
  tokenHighlighted: {
    backgroundColor: CCColors.Workbook.CreamGold,
  },
  tokenPrefix: {
    color: C.grey,
    marginRight: 2,
  },
  tokenPrefixActive: {
    color: ACTIVE_ACCENT,
  },
  tokenText: {
    fontWeight: '600',
  },
  tokenTextActive: {
    fontWeight: '700',
  },
  glyphSuffix: {
    fontWeight: '700',
    marginLeft: 1,
  },
  emptyToken: {
    paddingVertical: 1,
    paddingHorizontal: 5,
    margin: 1,
  },
  emptyTokenText: {
    fontSize: 11,
    color: C.greyLight,
    fontStyle: 'italic',
  },

  /* ── Interrupt strip (comment + variation lines, full width) ── */
  interruptWrap: {
    backgroundColor: INTERRUPT_BG,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: BORDER,
  },
  interruptSection: {
    paddingVertical: 5,
    paddingLeft: INDEX_WIDTH + 4,
    paddingRight: 8,
    gap: 2,
  },

  /* ── Comment block ── */
  commentBlock: {
    borderLeftWidth: 3,
    borderRadius: 6,
    paddingLeft: 8,
    paddingRight: 8,
    paddingVertical: 5,
    marginVertical: 1,
  },
  commentBlockText: {
    fontSize: 10.5,
    fontStyle: 'italic',
    lineHeight: 15,
  },

  /* ── Variation lines ── */
  lines: {
    marginVertical: 0,
    gap: 2,
  },
  line: {
    flexDirection: 'row',
    alignItems: 'stretch',
    marginVertical: 1,
  },
  lineRail: {
    width: 3,
    borderRadius: 1.5,
    marginRight: 7,
    opacity: 0.65,
  },
  lineRow: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  roleTag: {
    paddingVertical: 0,
    paddingHorizontal: 5,
    borderRadius: 7,
    marginRight: 3,
  },
  roleTagText: {
    fontSize: 8.5,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  paren: {
    color: C.grey,
    fontWeight: '600',
    marginHorizontal: 0,
  },
  inlineComment: {
    fontStyle: 'italic',
    marginRight: 3,
    flexShrink: 1,
  },
});

export default WorkbookMovesList;
